<template>
    <div class="price-section">
        <div class="price-section-room-header">
            <Translation
                element="strong"
                :translate="{
                    key: 'shared.room',
                    suffix: ` ${index + 1}`,
                    format: LabelFormat.Capitalize,
                }" />

            <TruncateText v-if="room.Description" :text="room.Description" :rows="1" />
        </div>

        <div class="price-section-rows">
            <template v-if="room.TaxesAndFees.length">
                <PriceSpecificationRow
                    v-if="roomExcludingTaxAndFeesPricing"
                    :price="roomExcludingTaxAndFeesPricing.price"
                    :description="t({ key: 'checkout.price_excluding_taxes_and_fees' })" />

                <div v-for="(fee, feeIndex) in room.TaxesAndFees" :key="feeIndex">
                    <PriceSpecificationRow
                        :description="getFeeLabel(fee)"
                        :price="fee.Amount" />
                </div>
            </template>
            <PriceSpecificationRow
                :price="roomTotalPricing.price"
                class="total"
                :description="t({ key: 'checkout.total_to_pay' })" />

            <PriceSpecificationRow
                v-if="room.PriceAtLocation && room.PriceAtLocation.Amount > 0"
                :price="room.PriceAtLocation"
                class="sub"
                :description="t({ key: 'checkout.pay_on_site' })" />
        </div>
        <Divider color="lightGrey" bottomMargin="xl" topMargin="xl" thickness="1" />
    </div>
</template>

<script lang="ts">
    import { defineComponent, PropType, computed } from 'vue';
    import { HotelProduct, Room, TaxOrFee } from '@/api/Cart/cart.interfaces';
    import { getHotelRoomPricing } from '@/helpers/price';
    import { t } from '@shared/helpers/translation';
    import { LabelFormat } from '@shared/interfaces/translation';
    import PriceSpecificationRow from '../PriceSpecificationRow.vue';
    import Divider from '@ui/components/Divider/Divider.vue';
    import TruncateText from '@ui/components/TruncateText/TruncateText.vue';
    import Translation from '@ui/components/Translation/Translation.vue';

    export default defineComponent({
        name: 'PriceSpecificationRoom',
        methods: { t },
        components: {
            Translation,
            TruncateText,
            Divider,
            PriceSpecificationRow,
        },
        props: {
            room: {
                type: Object as PropType<Room>,
                required: true,
            },
            product: {
                type: Object as PropType<HotelProduct>,
                required: true,
            },
            index: {
                type: Number,
                required: true,
            },
        },
        setup(props) {
            const roomTotalPricing = computed(() =>
                getHotelRoomPricing(props.product, props.room.Price)
            );

            const roomExcludingTaxAndFeesPricing = computed(() =>
                props.room.PriceExcludingTaxAndFees
                    ? getHotelRoomPricing(props.product, props.room.PriceExcludingTaxAndFees)
                    : undefined
            );

            const getFeeLabel = (fee: TaxOrFee) => {
                const base = t({ key: `taxes_and_fees.${fee.Type}` });

                return `${base} (${
                    !fee.IsIncludedInPrice
                        ? t({ key: 'checkout.not_included' })
                        : t({ key: 'checkout.included' })
                })`;
            };

            return {
                roomTotalPricing,
                roomExcludingTaxAndFeesPricing,
                getFeeLabel,
                LabelFormat,
            };
        },
    });
</script>

<style lang="scss">
    @forward 'PriceSpecificationRoom';
</style>
